import { Injectable } from '@angular/core';
import { db, GenericRecord } from '@app/offline-tools/dexie-db/app-storage-db';

import { ReportSearchParamRecord } from '@app/models';

@Injectable({ providedIn: 'root' })
export class ReportSearchStorageService {

  expireDuration = 1000 * 60 * 60 * 2; // Adjust record expires time here if needed.

  // Add modules that have profile setup for report module search stuff. Components will read this to decide is stuff should be set.
  public modulesSetupForReportParams: string[] = ['accounts', 'jobs', 'leads', 'subs', 'tasks'];

  constructor() { }

  // REPORT SEARCH PARAMS.
  async saveSearchParams(_params: ReportSearchParamRecord): Promise<any> {
    if (!_params) return;

    try {
      await db?.reportSearchParams?.clear(); // Make sure this is the only one.

      await db?.reportSearchParams?.put({
        _id: _params._id || 'currentSearch',
        ..._params,
        createdAt: new Date(),
        expiresAt: _params.expiresAt || new Date(Date.now() + this.expireDuration)
      });
    } catch (err) {
      console.log('Error saving report search params:', err);
    }
  }


  async getSearchParamByModule(_moduleName: string): Promise<ReportSearchParamRecord | null> {
    if (!_moduleName?.length) return null;

    try {
      const record = await db?.reportSearchParams?.where('moduleName')?.equals(_moduleName)?.first();
      return record || null;
    } catch (err) {
      console.log('Error fetching report search params by module:', err);
      return null;
    }
  }


  async getSearchParamById(_id: string = 'currentSearch'): Promise<ReportSearchParamRecord | null> {
    if (!_id?.length) return null;

    try {
      const record = await db?.reportSearchParams?.get(_id);
      return record || null;
    } catch (err) {
      console.log('Error fetching report search params by ID:', err);
      return null;
    }
  }


  async clearSearchParamById(_id: string = 'currentSearch'): Promise<void> {
    if (!_id?.length) return;

    try {
      await db?.reportSearchParams?.delete(_id);
    } catch (err) {
      console.log('Error clearing report search param by ID:', err);
    }
  }


  async clearSearchParams() {
    try {
      await db?.reportSearchParams?.clear();
    } catch (err) {
      console.log('Error clearing report search params:', err);
    }
  }


  async getCurrentSearchForModule(_moduleName: string, _returnWithEmptyPageResults: boolean = false): Promise<any | null> {
    // console.log('_moduleName:', _moduleName);
    try {
      const matchingSearchParam = await this.getSearchParamByModule(_moduleName);
      // console.log('matchingSearchParam:', matchingSearchParam);

      if (!matchingSearchParam) return null; // Return null if not found.

      // Build data to mock what a regular grid search looks like.
      const searchParams = {
        inclusionSearch: matchingSearchParam?.inclusionSearch || {},
        pageNumber: matchingSearchParam?.pageNumber ?? 0,
        pageSize: matchingSearchParam?.pageSize ?? 0,
        sortField: matchingSearchParam?.sortField || 'name',
        sortOrder: matchingSearchParam?.sortOrder || 'asc',
        sortDirection: matchingSearchParam?.sortOrder || 'asc'
      };

      // Add loose search term values.
      for (const [key, term] of Object.entries(matchingSearchParam?.inclusionSearch || {})) {
        if (term && term['value']) searchParams[key] = term['value'];
      }

      // Get records for current page.
      const storedPageResults = await this.getPagedReportRecords(searchParams.pageNumber, searchParams.pageSize);
      const searchResults = {
        message: storedPageResults?.message ?? [],
        pages: matchingSearchParam?.pages ?? storedPageResults?.pages ?? 0,
        totalItemCount: matchingSearchParam?.totalItemCount ?? storedPageResults?.totalItemCount ?? 0
      };

      // console.log('searchResults:', searchResults);

      if (!_returnWithEmptyPageResults && !searchResults?.message?.length) return null; // If false and there are no records in results, return null.

      return { params: searchParams, results: searchResults }; // Return mocked search data.
    } catch (err) {
      console.log('Error clearing report search params:', err);
      return null;
    }
  }


  // REPORT SEARCH RECORDS.
  async addReportRecords(records: GenericRecord[]) {
    if (!records?.length) return;
    try {
      await db?.reportSearchRecords?.clear();

      // Dexie does not keep them in the original order you add them.
      // Add an original order index and an expiry timestamp without mutating originals.
      const recordsWithIndexAndExpiry = records.map((r, i) => ({
        ...r,
        _originalOrderIndex: i,
        expiresAt: new Date(Date.now() + this.expireDuration),
      }));

      await db?.reportSearchRecords?.bulkPut(recordsWithIndexAndExpiry); // Store in IndexDb.
    } catch (err) {
      console.log('Error adding lead report records:', err);
    }
  }


  async clearReportRecords() {
    try {
      await db?.reportSearchRecords?.clear();
    } catch (err) {
      console.log('Error clearing report records:', err);
    }
  }


  async getReportRecord(_id: string): Promise<GenericRecord | null> {
    try {
      return await db?.reportSearchRecords?.get(_id) ?? null;
    } catch (err) {
      console.log('Error getting report record:', err);
      return null;
    }
  }


  async getAllReportRecords(): Promise<GenericRecord[]> {
    try {
      return await db?.reportSearchRecords?.toArray() ?? [];
    } catch (err) {
      console.log('Error getting all report records:', err);
      return [];
    }
  }


  getPagedReportRecords(_pageNumber: number = 0, _pageSize: number = 100, _returnRecordsOnly: boolean = false): Promise<any> {
    return new Promise(async (resolve) => {
      // console.log('_pageNumber:', _pageNumber);
      // console.log('_pageSize:', _pageSize);

      try {
        // Get records in the order they were entered.
        const allRecords = await db?.reportSearchRecords?.orderBy('_originalOrderIndex')?.toArray() ?? [];

        // Determine which records belong to the current page number.
        const start = _pageNumber * _pageSize;
        const totalPages = Math.max(Math.ceil(allRecords.length / _pageSize), 1);
        const pagedRecords = allRecords.slice(start, start + _pageSize);
        // console.log('pagedRecords: ', pagedRecords);

        // Return object structure like a datasource search unless told to return records only.
        if (!_returnRecordsOnly) resolve({ message: pagedRecords, pages: totalPages, totalItemCount: allRecords.length });
        else resolve(pagedRecords);
      } catch (err) {
        console.log('Error fetching paged report records:', err);
        if (!_returnRecordsOnly) resolve({ message: [], pages: 0, totalItemCount: 0 });
        else resolve([]);
      }
    });
  }
}